import { f7 } from 'framework7-react';
import { getT } from '@/i18n';

// ── Unsaved-changes registry + navigation guard ──────────────────────────────
// A settings detail form publishes its "dirty" state here (via useUnsavedGuard /
// SettingsSaveNav). Only ONE settings detail form is ever mounted at a time
// (master-detail), so a single module-level flag is enough - no per-route map.
//
// Every navigation entry point that can leave a form (the settings nav rows and
// Back via the routes' `beforeLeave`, the sidebar/rail + "Done" via navigateMain,
// and logout) calls `confirmDiscardChanges()` first and only proceeds if it
// resolves true.

let dirty = false;

export function setUnsavedDirty(value: boolean): void {
  dirty = value;
}

export function hasUnsavedChanges(): boolean {
  return dirty;
}

/**
 * Ask the user before throwing away unsaved edits.
 * Resolves `true` to PROCEED with navigation, `false` to CANCEL it.
 *
 * When there is nothing dirty it resolves `true` immediately. On confirm it
 * clears the flag, so a single user action that cascades into several navigations
 * (e.g. "Done" → reloadAll, which also trips a route `beforeLeave`) only prompts
 * once.
 */
export function confirmDiscardChanges(): Promise<boolean> {
  if (!dirty) return Promise.resolve(true);
  // Resolve strings from the CURRENT locale at call time (not module load
  // time) - `getT()` reads the i18n store's live state, so this reflects
  // whatever language is active when the dialog is actually shown.
  const t = getT();
  return new Promise((resolve) => {
    // `dialog.create` lets us give this dialog its own button text while
    // keeping the same layout/behavior (Cancel first, strong confirm second,
    // Enter/Esc shortcuts) as the framework's built-in confirm.
    f7.dialog
      .create({
        title: t.unsavedChangesTitle,
        text: t.unsavedChangesText,
        destroyOnClose: true,
        buttons: [
          {
            text: t.cancel,
            keyCodes: [27],
            onClick: () => resolve(false),
          },
          {
            text: t.discardChanges,
            strong: true,
            keyCodes: [13],
            onClick: () => {
              dirty = false;
              resolve(true);
            },
          },
        ],
      })
      .open();
  });
}
